import { cn } from "@/utils/cn";
import { ChevronDown, ChevronRight, Circle, CornerDownRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { MapProgress } from "../hooks/useMapProgress";
import type { TraceToolCall } from "../types";
import { parseToolOutput } from "../utils/toolOutput";
import { summarizeShellCommand } from "../utils/toolSummary";

const NOT_FOUND_PATTERNS = [
  /not found/i,
  /no such file or directory/i,
  /does not exist/i,
  /no results/i,
  /no matches/i,
  /no [\w\s]+ found/i,
];

const isInfoNotError = (msg: string): boolean => {
  return NOT_FOUND_PATTERNS.some((pattern) => pattern.test(msg));
};

const toolName = (toolCall: TraceToolCall): string => {
  const rawName = toolCall.name.includes("__")
    ? toolCall.name.split("__").pop()!
    : toolCall.name;
  return rawName.toLowerCase();
};

/** A call that starts a subagent, such as a Data Curation grid writer. */
const isSubagentCall = (toolCall: TraceToolCall): boolean => {
  const name = toolName(toolCall);
  return name === "agent" || name === "task";
};

const formatToolCall = (toolCall: TraceToolCall): string => {
  const input = toolCall.input;
  const isActive =
    toolCall.status === "pending" || toolCall.status === "running";

  if (input && typeof input._raw === "string") {
    return toolCall.status === "stopped" ? "Unfinished step" : "Working...";
  }

  if (!input && isActive) {
    return "Working...";
  }

  if (isSubagentCall(toolCall)) {
    const description =
      typeof input?.description === "string" ? input.description.trim() : "";
    return description ? `Subagent: ${description}` : "Subagent";
  }

  const name = toolName(toolCall);

  const isShell = name === "bash" || name === "papers_bash" || name === "sh";
  if (isShell && input && typeof input.command === "string") {
    let cmd = input.command;
    if (cmd.startsWith("paperclip ")) {
      cmd = cmd.slice("paperclip ".length);
    }
    return cmd;
  }

  if (isActive) {
    return "Working...";
  }

  if (name === "skill") {
    const skillName = input
      ? (Object.values(input).find((v) => typeof v === "string") as
        | string
        | undefined)
      : undefined;
    return skillName ? `skill: ${skillName}` : "skill";
  }

  const displayNames: Record<string, string> = {
    bash: "Bash",
    papers_bash: "Bash",
  };
  const displayName = displayNames[name] || name;

  if (input) {
    const args = Object.entries(input)
      .filter(
        ([k, v]) =>
          v !== undefined && v !== null && k !== "_raw" && k !== "description",
      )
      .map(([, v]) => {
        const val = typeof v === "string" ? v : JSON.stringify(v);
        return val;
      })
      .join(", ");
    return args ? `${displayName}(${args})` : displayName;
  }

  return displayName;
};

/**
 * The raw shell command string for a bash-style tool call (before the
 * `paperclip ` prefix strip), or null for non-shell tools. Used to derive the
 * semantic label and to show the underlying command on expand.
 */
const getShellCommand = (toolCall: TraceToolCall): string | null => {
  const input = toolCall.input;
  const name = toolName(toolCall);
  const isShell = name === "bash" || name === "papers_bash" || name === "sh";
  if (isShell && input && typeof input.command === "string") {
    return input.command;
  }
  return null;
};

const extractResultSummary = (toolCall: TraceToolCall): string | null => {
  if (toolCall.status !== "complete" && toolCall.status !== "error")
    return null;
  if (!toolCall.output) return null;
  const output = parseToolOutput(toolCall.output);

  const filterSubline = output.match(/→\s*(\d+)\s+papers?\s+after\s+filtering/);
  if (filterSubline) return `${filterSubline[1]} papers after filtering`;

  const searchSubline = output.match(
    /→\s*(\d+)\s+papers?\s+\(ranked\s+by\s+relevance/,
  );
  if (searchSubline) return `${searchSubline[1]} papers found`;

  const genericSubline = output.match(/→\s*(\d+)\s+papers?\s*$/m);
  if (genericSubline) return `${genericSubline[1]} papers found`;

  const accMatch = output.match(/\((\d+)\s+unique\s+papers?\)/);
  if (accMatch) return `${accMatch[1]} papers found`;

  const hitsMatch = output.match(/(\d+)\s+total\s+hits/);
  if (hitsMatch) return `${hitsMatch[1]} hits`;

  const filterCumMatch = output.match(/→\s*(\d+)\s+total\s+approved/);
  if (filterCumMatch) return `${filterCumMatch[1]} papers after filtering`;

  const filterMatch = output.match(/Filtered:\s*(\d+)\s*→\s*(\d+)\s+papers?/);
  if (filterMatch) return `${filterMatch[2]} papers after filtering`;

  const sfMatch = output.match(
    /(\d+)\s+searched\s*→\s*(\d+)\s+relevant\s*→\s*(\d+)\s+returned/,
  );
  if (sfMatch)
    return `${sfMatch[1]} → ${sfMatch[2]} relevant → ${sfMatch[3]} returned`;

  const m = output.match(/Found\s+(\d+)\s+(papers?|documents?)/i);
  if (m) return `${m[1]} ${m[2]} found`;

  const mapMatch = output.match(/Map complete:\s*(\d+)\/(\d+)\s+tasks/);
  if (mapMatch) return `${mapMatch[1]}/${mapMatch[2]} tasks`;

  try {
    const parsed = JSON.parse(toolCall.output);
    if (parsed.exit_code && parsed.exit_code !== 0) {
      const msg = (parsed.stderr || parsed.stdout || "").trim();
      if (msg) {
        const firstLine = msg.split("\n")[0];
        if (isInfoNotError(firstLine)) {
          return firstLine.length > 120
            ? firstLine.slice(0, 117) + "..."
            : firstLine;
        }
      }
    }
  } catch {
    /* not JSON */
  }

  if (toolCall.status === "error") {
    const firstLine = output.trim().split("\n")[0];
    if (firstLine && isInfoNotError(firstLine)) {
      return firstLine.length > 120 ? firstLine.slice(0, 117) + "..." : firstLine;
    }
  }

  return null;
};

const extractError = (toolCall: TraceToolCall): string | null => {
  if (!toolCall.output) return null;
  if (toolCall.status !== "complete" && toolCall.status !== "error") return null;
  try {
    const parsed = JSON.parse(toolCall.output);
    if (parsed.success === false) {
      const msg = (
        parsed.result?.output ||
        parsed.error ||
        ""
      ).trim();
      if (!msg) return "Tool execution failed";
      const firstLine = msg.split("\n").pop() || msg.split("\n")[0];
      if (isInfoNotError(firstLine)) return null;
      return firstLine.length > 120 ? firstLine.slice(0, 117) + "..." : firstLine;
    }
    if (parsed.exit_code && parsed.exit_code !== 0) {
      const msg = (parsed.stderr || parsed.stdout || "").trim();
      if (!msg) return `Exit code ${parsed.exit_code}`;
      const firstLine = msg.split("\n")[0];
      if (isInfoNotError(firstLine)) return null;
      return firstLine.length > 120 ? firstLine.slice(0, 117) + "..." : firstLine;
    }
  } catch {
    /* not JSON */
  }
  if (toolCall.status === "error") {
    const output = toolCall.output.trim();
    if (!output) return "Tool call failed";
    const lines = output.split("\n").filter((l) => l.trim());
    const errorLine = lines.find((l) => l.includes("[error]")) || lines[lines.length - 1] || lines[0];
    const cleaned = errorLine.replace(/^\[error\]\s*/, "").trim();
    if (isInfoNotError(cleaned)) return null;
    return cleaned.length > 120 ? cleaned.slice(0, 117) + "..." : cleaned;
  }
  return null;
};

const hasError = (toolCall: TraceToolCall): boolean => {
  if (toolCall.status === "error") {
    const output = parseToolOutput(toolCall.output || "").trim();
    if (output && isInfoNotError(output.split("\n")[0])) return false;
    return true;
  }
  if (!toolCall.output) return false;
  try {
    const parsed = JSON.parse(toolCall.output);
    if (parsed.success === false) {
      const msg = (parsed.result?.output || parsed.error || "").trim();
      if (msg && isInfoNotError(msg.split("\n")[0])) return false;
      return true;
    }
    if (parsed.exit_code !== undefined && parsed.exit_code !== 0) {
      const msg = (parsed.stderr || parsed.stdout || "").trim();
      if (msg && isInfoNotError(msg.split("\n")[0])) return false;
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

interface Props {
  toolCall: TraceToolCall;
  mapProgress?: MapProgress | null;
}

export const ToolCallItem = ({ toolCall, mapProgress }: Props) => {
  const isActive =
    toolCall.status === "pending" || toolCall.status === "running";
  const isStopped = toolCall.status === "stopped";
  const isError = !isActive && hasError(toolCall);
  // Prefer a human-readable summary of what the agent is doing ("Searching
  // PubMed Central", "Reading a paper"); the raw command is revealed on expand.
  // Computed from the moment the arguments are available (not just on
  // completion) so the row never flips from raw command to readable text.
  const rawLabel = formatToolCall(toolCall);
  const shellCommand = getShellCommand(toolCall);
  const semanticLabel = shellCommand ? summarizeShellCommand(shellCommand) : null;
  const displayText = semanticLabel ?? rawLabel;
  const [expanded, setExpanded] = useState(false);

  // The row label fades at the right edge when it's too long to fit. Let the
  // user scroll horizontally *within the row* to read to the end, and drop the
  // fade once they've scrolled all the way over so the last text is legible.
  const codeRef = useRef<HTMLElement>(null);
  const [showFade, setShowFade] = useState(false);
  const updateFade = useCallback(() => {
    const el = codeRef.current;
    if (!el) return;
    const overflowing = el.scrollWidth - el.clientWidth > 1;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setShowFade(overflowing && !atEnd);
  }, []);
  useEffect(() => {
    updateFade();
    const el = codeRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(updateFade);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateFade, displayText]);

  const isComplete = !isActive;
  const parsedOutput = toolCall.output;
  const hasOutput = isComplete && !!parsedOutput && parsedOutput.trim().length > 0;
  const children = toolCall.children ?? [];
  const stepsLabel = children.length === 1 ? "1 step" : `${children.length} steps`;

  const rowClassName = cn(
    "flex w-full items-center gap-1.5 rounded px-2 py-0 -mx-1 transition-colors",
    isComplete &&
    "cursor-pointer text-left hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400",
  );

  const rowContent = (
    <>
      {/* Spinner (12px) vs dot (8px): nudge active rows left so circles and text
          align with completed rows; extra dot margin closes the remaining gap.
          Use translate on this inner wrapper so the row hover background stays put. */}
      <span
        className={cn(
          "flex min-w-0 flex-1 items-center gap-1.5",
          isActive && "-translate-x-0.5",
        )}
      >
        {isActive ? (
          mapProgress ? (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin text-blue-500" aria-hidden />
          ) : (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin text-amber-500" aria-hidden />
          )
        ) : (
          <Circle
            className={cn(
              "h-2 w-2 shrink-0 mr-0.5",
              isError
                ? "fill-red-500 text-red-500"
                : isStopped
                  ? "fill-transparent text-zinc-400"
                  : "fill-emerald-500 text-emerald-500",
            )}
            aria-hidden
          />
        )}
        <code
          ref={codeRef}
          onScroll={updateFade}
          className="text-xs leading-snug text-zinc-500 overflow-x-auto toolrow-scroll whitespace-nowrap flex-1 min-w-0"
          style={
            showFade
              ? { maskImage: "linear-gradient(to right, black 92%, transparent 100%)", WebkitMaskImage: "linear-gradient(to right, black 92%, transparent 100%)" }
              : undefined
          }
        >
          {displayText}
        </code>
      </span>
      {isComplete && (
        expanded
          ? <ChevronDown className="h-3 w-3 shrink-0 text-zinc-400" aria-hidden />
          : <ChevronRight className={cn("h-3 w-3 shrink-0", hasOutput || children.length > 0 ? "text-zinc-400" : "text-zinc-200")} aria-hidden />
      )}
    </>
  );

  return (
    <div className="ml-2">
      {isComplete ? (
        <button
          type="button"
          className={rowClassName}
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-label={`${expanded ? "Collapse" : "Expand"} tool output: ${displayText}`}
        >
          {rowContent}
        </button>
      ) : (
        <div className={rowClassName}>{rowContent}</div>
      )}

      {isActive && mapProgress && (() => {
        const { total, completed, failed, in_progress, started_at } = mapProgress;
        const done = completed + failed;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        const barWidth = 20;
        const filled = Math.round((pct / 100) * barWidth);
        const bar = "\u2588".repeat(filled) + "\u2591".repeat(barWidth - filled);

        let etaStr = "";
        if (started_at && done > 0) {
          const elapsed = Date.now() / 1000 - started_at;
          const rate = done / elapsed;
          const remaining = total - done;
          const eta = remaining / rate;
          const etaSec = Math.round(eta);
          const etaFmt =
            etaSec >= 60 ? `${Math.floor(etaSec / 60)}m${etaSec % 60}s` : `${etaSec}s`;
          etaStr = ` \u2022 ETA ${etaFmt}`;
        }

        const runningStr = done === 0 && in_progress > 0 ? ` \u2022 ${in_progress} running` : "";

        return (
          <div className="flex items-start gap-1 ml-[14px] mt-0.5">
            <span className="text-gray-400 text-3xs">⎿</span>
            <code className="text-3xs text-gray-500 font-mono">
              {total} papers [{bar}] {done}/{total} ({pct}%){etaStr}{runningStr}{failed > 0 ? ` (${failed} failed)` : ""}
            </code>
          </div>
        );
      })()}

      {children.length > 0 && (isActive || expanded) && (
        <div
          className="ml-3 border-l border-zinc-200 pl-1"
          role="group"
          aria-label={`Steps of ${displayText}`}
        >
          {children.map((child) => (
            <ToolCallItem key={child.id} toolCall={child} />
          ))}
        </div>
      )}

      {!isActive && (() => {
        const error = extractError(toolCall);
        return error ? (
          <div className="flex items-center gap-1.5 ml-5 mt-0.5">
            <CornerDownRight className="h-3 w-3 shrink-0 text-zinc-300" aria-hidden />
            <code className="text-xs text-zinc-500">{error}</code>
          </div>
        ) : null;
      })()}

      {!expanded && !isActive && !isError && (() => {
        const summary = isStopped
          ? "Stopped"
          : extractResultSummary(toolCall) ?? (children.length > 0 ? stepsLabel : null);
        return summary ? (
          <div className="flex items-center gap-1.5 ml-5 mt-0.5">
            <CornerDownRight className="h-3 w-3 shrink-0 text-zinc-300" aria-hidden />
            <code className="text-xs text-zinc-500">{summary}</code>
          </div>
        ) : null;
      })()}

      {expanded && semanticLabel && (
        <div className="ml-5 mt-1.5 mb-1">
          <code className="block text-xs text-zinc-600 font-mono bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 overflow-x-auto whitespace-pre-wrap break-all">
            <span className="select-none text-zinc-400">$ </span>
            {rawLabel}
          </code>
        </div>
      )}

      {expanded && hasOutput && (
        <div className="ml-5 mt-1.5 mb-1">
          <pre className="text-xs text-zinc-600 font-mono bg-zinc-50 border border-zinc-200 rounded-[3px] p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all leading-relaxed">
            {parseToolOutput(toolCall.output!)}
          </pre>
        </div>
      )}
      {expanded && !hasOutput && isComplete && (
        <div className="ml-5 mt-1.5 mb-1">
          <span className="text-xs text-zinc-500 italic">
            {isStopped ? "Stopped before it returned a result" : "No output captured"}
          </span>
        </div>
      )}
    </div>
  );
};
